import { Box, Button, IconButton, Typography } from '@mui/material'
import { IconX } from '@tabler/icons-react'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { logEvent } from '../../analytics/analytics'
import { ROUTES } from '../../helpers/routes-helper'

interface ReviewPromptBannerProps {
  restaurantId?: string
  alias: string
}

/**
 * Dismissible "Been here? Rate it" prompt on the restaurant detail page — the entry point for the
 * redesigned review flow (works for anonymous users too). Fires review_prompt_shown on display.
 */
const ReviewPromptBanner: React.FC<ReviewPromptBannerProps> = ({
  restaurantId,
  alias,
}) => {
  const navigate = useNavigate()
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    logEvent('review_prompt_shown', {
      restaurant_id: restaurantId ?? alias,
      screen_name: 'restaurant_detail',
    })
  }, [restaurantId, alias])

  if (dismissed) {
    return null
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        p: '12px 16px',
        mb: '12px',
        borderRadius: '12px',
        backgroundColor: (theme) => theme.palette.warning.light,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Been here? Rate it and help others discover great spots.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Button
          variant="contained"
          size="small"
          onClick={() => {
            navigate(`${ROUTES.RESTAURANT_DETAILS}/${alias}/details?name=review`)
          }}
        >
          Rate it
        </Button>
        <IconButton
          size="small"
          aria-label="dismiss"
          onClick={() => {
            setDismissed(true)
          }}
        >
          <IconX size={16} />
        </IconButton>
      </Box>
    </Box>
  )
}

export default ReviewPromptBanner
